import {
  Button as MuiButton,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  FormHelperText,
  Grid as MuiGrid,
  TextField,
  Typography as MuiTypography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Autocomplete } from '@material-ui/lab'
import { spacing } from '@material-ui/system'
import { AxiosPromise, AxiosRequestConfig } from 'axios'
import { RefetchOptions } from 'axios-hooks'
import { DropzoneAreaBase } from 'material-ui-dropzone'
import React, { ReactNode, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import styled from 'styled-components'
import * as yup from 'yup'
import { useForm } from '../../forms'
import { skuit } from '../../services/skuit'
import { FilePreview, UrlPreview } from '../FilePreview'
import { useQueryPaginator } from '../Table'
import { Notification } from '../Notification'

const Button = styled(MuiButton)(spacing)
const Grid = styled(MuiGrid)(spacing)
const Typography = styled(MuiTypography)(spacing)

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

export interface AttachmentProp {
  file: File
  tags: string[]
}

interface OrderProps {
  attachments: AttachmentProp[]
}

const orderSchema = yup.object().shape({
  attachments: yup.array().of(
    yup
      .object({
        xid: yup.string().when('file', {
          is: (file) => !file,
          then: yup.string().required(),
        }),
        file: yup.mixed(),
        tags: yup.array().of(yup.string()),
      })
      .label('Attachments')
  ),
})

interface ImageListWrapperProps {
  children: ReactNode
}

const ImageListWrapper = (props: ImageListWrapperProps) => {
  const { children } = props
  return (
    <Grid container spacing={6} mt={6}>
      {children}
    </Grid>
  )
}

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  onSuccess: () => void
  addAttachment: (
    config?: AxiosRequestConfig | undefined,
    options?: RefetchOptions | undefined
  ) => AxiosPromise<any>
}

export const AddAttachmentsDialog = (props: Props) => {
  const classes = useStyles()
  const { open, handleClose, onSave, onSuccess, addAttachment } = props

  const [saving, setSaving] = useState(false)

  const { Form, Field, FieldList, data: formData } = useForm(orderSchema)

  useEffect(() => {
    formData.setValue('attachments', [])
  }, [open, formData])

  const tagPaginator = useQueryPaginator('tags', skuit.client.attachments.tags.list)

  const onSubmit = (data: OrderProps) => {
    setSaving(true)
    addAttachment({ data })
      .then((response) => {
        handleClose()
        if (onSave) {
          onSave()
        }
        onSuccess()
        toast.success(() => (
          <Notification
            message={`Successfully added attachments: ${response.data.name}`}
            newObject={response.data}
          />
        ))
      })
      .catch((error) => {
        if (error?.response?.data?.details) {
          toast.error(() => (
            <Notification
              message={`Error adding the attachments: ${error.response.data.details}`}
            />
          ))
        } else if (error?.response?.status === 500) {
          toast.error(() => (
            <Notification message="Error adding the attachments: An unknown error has ocurred" />
          ))
        }
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogContent>
          <Typography variant="h6" mt={6} mb={3}>
            Attachments
          </Typography>
          <FieldList
            name="attachments"
            startCount={0}
            renderHeader={({ name, value, setValue }) => (
              <DropzoneAreaBase
                dropzoneText="Drag and drop or click to add files"
                fileObjects={[]}
                onAdd={(files) => {
                  const fileData = files.map((fileObj) => {
                    return { file: fileObj.file, tags: [] }
                  })
                  const newValue = (value || []).concat(fileData)
                  setValue(name, newValue)
                }}
                filesLimit={10}
                maxFileSize={75000000}
                showPreviewsInDropzone={false}
              />
            )}
            renderList={ImageListWrapper}
            renderField={({ name: itemName }) => (
              <Grid item xs={3} key={itemName}>
                <Card>
                  <CardContent>
                    <Field
                      name="file"
                      renderField={({ value, remove, error }) => {
                        if (!value) {
                          return null
                        }
                        return (
                          <>
                            <FilePreview file={value} onDelete={() => remove(itemName)} />
                            {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                          </>
                        )
                      }}
                    />
                    <Field
                      name="url"
                      renderField={({ value, remove, error }) => {
                        if (!value) {
                          return null
                        }
                        return (
                          <>
                            <UrlPreview
                              url={value}
                              allowDownload={false}
                              onDelete={() => remove(itemName)}
                            />
                            {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                          </>
                        )
                      }}
                    />
                    <Field
                      name="tags"
                      getSubmitValue={(value) =>
                        value && value.map((tag: { slug: string }) => tag.slug)
                      }
                      renderField={({ name, value, setValue, error }) => (
                        <Autocomplete
                          id={name}
                          multiple
                          freeSolo
                          options={tagPaginator.data.results}
                          getOptionLabel={(tag) => tag.name}
                          filterSelectedOptions
                          onChange={(event: any, newValue: any | null) => {
                            setValue(name, newValue)
                          }}
                          value={value === '' ? [] : value}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={!!error}
                              helperText={error}
                              label="Tags"
                            />
                          )}
                        />
                      )}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" disabled={saving} autoFocus>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
